/**
 * Open-ticket cart draft — newest/touched lines on top; survive refresh
 * for the open drawer (device-local; plan/28 Floor).
 */

export type CartDraftLine = {
  productId: string;
  name: string;
  unitPrice: string;
  qty: number;
  trackLot?: boolean;
  trackSerial?: boolean;
  lotCode?: string;
  serialCode?: string;
};

const CART_PREFIX = "ix_pos_cart:";

export function cartDraftStorageKey(
  companyRef: string,
  terminalId: string,
  drawerId: string,
): string {
  return `${CART_PREFIX}${companyRef}:${terminalId}:${drawerId}`;
}

export function addOrBumpCartLine(
  prev: readonly CartDraftLine[],
  next: CartDraftLine,
): CartDraftLine[] {
  const existing = prev.find((l) => l.productId === next.productId);
  if (existing) {
    if (existing.trackSerial || next.trackSerial) return [...prev];
    const bumped: CartDraftLine = {
      ...existing,
      qty: existing.qty + next.qty,
      unitPrice: next.unitPrice,
      name: next.name,
    };
    return [bumped, ...prev.filter((l) => l.productId !== next.productId)];
  }
  return [next, ...prev];
}

function isCartDraftLine(x: unknown): x is CartDraftLine {
  if (x == null || typeof x !== "object") return false;
  const o = x as Record<string, unknown>;
  return (
    typeof o.productId === "string" &&
    o.productId.length > 0 &&
    typeof o.name === "string" &&
    typeof o.unitPrice === "string" &&
    typeof o.qty === "number" &&
    Number.isFinite(o.qty) &&
    o.qty > 0
  );
}

export function parseCartDraft(raw: string | null): CartDraftLine[] {
  if (raw == null || raw.trim() === "") return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isCartDraftLine).map((l) => ({
      productId: l.productId,
      name: l.name,
      unitPrice: l.unitPrice,
      qty: l.qty,
      ...(l.trackLot ? { trackLot: true } : {}),
      ...(l.trackSerial ? { trackSerial: true } : {}),
      ...(typeof l.lotCode === "string" ? { lotCode: l.lotCode } : {}),
      ...(typeof l.serialCode === "string" ? { serialCode: l.serialCode } : {}),
    }));
  } catch {
    return [];
  }
}

export function serializeCartDraft(lines: readonly CartDraftLine[]): string {
  return JSON.stringify([...lines]);
}

export function loadCartDraft(key: string): CartDraftLine[] {
  if (typeof localStorage === "undefined") return [];
  try {
    return parseCartDraft(localStorage.getItem(key));
  } catch {
    return [];
  }
}

export function saveCartDraft(
  key: string,
  lines: readonly CartDraftLine[],
): void {
  if (typeof localStorage === "undefined") return;
  try {
    if (lines.length === 0) {
      localStorage.removeItem(key);
      return;
    }
    localStorage.setItem(key, serializeCartDraft(lines));
  } catch {
    /* quota / private mode */
  }
}

export function clearCartDraft(key: string): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}
